import { useState } from 'react';
import { X, ExternalLink, FileText, Sparkles, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VerifiedBadge } from '@/components/tools/VerifiedBadge';

export function ArcusBanner() {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) return null;

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-[hsl(250,70%,18%)] via-[hsl(260,75%,28%)] to-[hsl(280,70%,30%)]">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/3 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-fuchsia-200 rounded-full blur-3xl animate-pulse delay-700" />
      </div>

      <div className="container mx-auto px-4 py-5 relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Logo */}
            <a
              href="https://getarcus.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <div className="w-14 h-14 rounded-xl shadow-lg ring-2 ring-white/20 bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <FileSignature className="h-7 w-7 text-white" />
              </div>
            </a>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-100 bg-white/15 px-2 py-0.5 rounded-full backdrop-blur-sm">
                  <Sparkles className="h-3 w-3" />
                  NEW
                </span>
                <span className="text-xs text-white/70 hidden sm:inline">Featured Tool</span>
              </div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-bold text-lg leading-tight">
                  Arcus — The proposal your client actually signs
                </h3>
                <VerifiedBadge tier="pro" size="sm" className="text-white fill-white/20" />
              </div>
              <p className="text-white/80 text-sm mt-0.5 hidden sm:block">
                AI-powered presales: brief in, signed proposal out. Built for IT services, agencies & integrators.
              </p>
            </div>
          </div>

          {/* Feature pills - hidden on mobile */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <span className="inline-flex items-center gap-1 text-xs text-white/90 bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-sm">
              <FileText className="h-3.5 w-3.5 text-fuchsia-200" />
              RFP Intelligence
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-white/90 bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-sm">
              <FileSignature className="h-3.5 w-3.5 text-violet-200" />
              Client Sign Portal
            </span>
          </div>

          {/* CTA */}
          <a
            href="https://getarcus.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-[hsl(260,75%,28%)] px-5 py-2.5 rounded-full text-sm font-bold hover:bg-white/90 transition-all hover:scale-105 shadow-lg"
          >
            Try Arcus
            <ExternalLink className="h-3.5 w-3.5" />
          </a>

          {/* Dismiss */}
          <Button
            variant="ghost"
            size="icon"
            className="text-white/60 hover:text-white hover:bg-white/10 h-8 w-8 flex-shrink-0"
            onClick={() => setIsDismissed(true)}
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
